/**
 * Campaign Service - All API endpoints for campaign management
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  "Content-Type": "application/json",
});

const getFormDataHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

/**
 * 1. Create Campaign (POST)
 * POST /api/appointments/createCampaign
 */
export async function createCampaign(campaignData) {
  try {
    const formData = new FormData();

    // Add all fields to FormData
    formData.append("contactGroup", campaignData.contactGroup || "");
    formData.append("campaignSubject", campaignData.campaignSubject || "");
    formData.append("isEmail", String(campaignData.isEmail || false));
    // formData.append("isSms", String(campaignData.isSms || false));
    formData.append("emailBody", campaignData.emailBody || "");
    // formData.append("smsBody", campaignData.smsBody || "");
    formData.append("campaignName", campaignData.campaignName || "");
    formData.append("agencyName", campaignData.agencyName || "");

    if (campaignData.scheduledTime) {
      formData.append("scheduledTime", campaignData.scheduledTime);
    }
    if (campaignData.status) {
      formData.append("status", campaignData.status);
    }

    // Add attachments if present
    if (campaignData.attachments && Array.isArray(campaignData.attachments)) {
      campaignData.attachments.forEach((file) => {
        if (file instanceof File) {
          formData.append("attachments", file);
        }
      });
    }

    const response = await fetch(
      `${API_BASE}/api/appointments/createCampaign`,
      {
        method: "POST",
        headers: getFormDataHeaders(),
        body: formData,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Create campaign error:", error);
    throw error;
  }
}

/**
 * 2. Estimate Contact Group Count (GET)
 * GET /api/appointments/estimateContacts?contactGroup=["All"]
 */
export async function estimateContacts(contactGroup) {
  try {
    const params = new URLSearchParams();
    if (Array.isArray(contactGroup)) {
      params.append("contactGroup", JSON.stringify(contactGroup));
    } else {
      params.append("contactGroup", contactGroup);
    }

    const response = await fetch(
      `${API_BASE}/api/appointments/estimateContacts?${params.toString()}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Estimate contacts error:", error);
    throw error;
  }
}

/**
 * 3. Get Contact Groups (GET)
 * GET /api/appointments/getContactGroups
 */
export async function getContactGroups() {
  try {
    const response = await fetch(
      `${API_BASE}/api/appointments/getContactGroups`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Get contact groups error:", error);
    throw error;
  }
}

/**
 * 4. Get Recent Campaigns (GET)
 * GET /api/appointments/getRecentCampaigns
 */
export async function getRecentCampaigns() {
  try {
    const response = await fetch(
      `${API_BASE}/api/appointments/getRecentCampaigns`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Get recent campaigns error:", error);
    throw error;
  }
}

/**
 * 5. Get Active Campaigns (GET)
 * GET /api/appointments/getActiveCampaigns
 */
export async function getActiveCampaigns() {
  try {
    const response = await fetch(
      `${API_BASE}/api/appointments/getActiveCampaigns`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Get active campaigns error:", error);
    throw error;
  }
}

/**
 * 6. Get Campaign Recipients (GET)
 * GET /api/appointments/getCampaignRecipients?campaignId=...&page=...&limit=...
 */
export async function getCampaignRecipients(campaignId, page = 1, limit = 10) {
  try {
    const params = new URLSearchParams();
    params.append("campaignId", campaignId);
    params.append("page", page);
    params.append("limit", limit);

    const response = await fetch(
      `${API_BASE}/api/appointments/getCampaignRecipients?${params.toString()}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Get campaign recipients error:", error);
    throw error;
  }
}

/**
 * 7. Get Single Campaign (GET)
 * GET /api/appointments/getSingleCampaign?campaignId=...
 */
export async function getCampaignById(campaignId) {
  try {
    const params = new URLSearchParams();
    params.append("campaignId", campaignId);

    const response = await fetch(
      `${API_BASE}/api/appointments/getSingleCampaign?${params.toString()}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Get campaign by ID error:", error);
    throw error;
  }
}

/**
 * 7. Get Contact Group Counts (GET)
 * GET /api/appointments/getContactGroupCounts?contactGroup=Student Visa, Employment Sponsorship
 */
export async function getContactGroupCounts(contactGroups) {
  try {
    const params = new URLSearchParams();
    if (Array.isArray(contactGroups)) {
      params.append("contactGroup", contactGroups.join(", "));
    } else {
      params.append("contactGroup", contactGroups);
    }

    const response = await fetch(
      `${API_BASE}/api/appointments/getContactGroupCounts?${params.toString()}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Get contact group counts error:", error);
    throw error;
  }
}

/****
 * 8. Stop Campaign (PUT)
 */
export async function stopCampaign(campaignId) {
  try {
    const response = await fetch(`${API_BASE}/api/appointments/stopCampaign`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify({ campaignId }),
    });

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Stop campaign error:", error);
    throw error;
  }
}

/**
 * Get Drafted Campaign (GET)
 * GET /api/appointments/getDraftedCampaign
 */
export async function getDraftedCampaign() {
  try {
    const response = await fetch(
      `${API_BASE}/api/appointments/getDraftedCampaign`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Get drafted campaign error:", error);
    throw error;
  }
}
/**
 * Update Campaign (PUT)
 * PUT /api/appointments/updateCampaign
 */
export async function updateCampaign(campaignId, campaignData) {
  try {
    const formData = new FormData();

    // Add campaign ID
    formData.append("campaignId", campaignId);

    // Add all fields to FormData
    formData.append("contactGroup", campaignData.contactGroup || "");
    formData.append("campaignSubject", campaignData.campaignSubject || "");
    formData.append("isEmail", String(campaignData.isEmail || false));
    // formData.append("isSms", String(campaignData.isSms || false));
    formData.append("emailBody", campaignData.emailBody || "");
    // formData.append("smsBody", campaignData.smsBody || "");
    formData.append("campaignName", campaignData.campaignName || "");
    formData.append("agencyName", campaignData.agencyName || "");

    if (campaignData.scheduledTime) {
      formData.append("scheduledTime", campaignData.scheduledTime);
    }
    if (campaignData.status) {
      formData.append("status", campaignData.status);
    }

    // Add attachments if present
    if (campaignData.attachments && Array.isArray(campaignData.attachments)) {
      campaignData.attachments.forEach((file) => {
        if (file instanceof File) {
          formData.append("attachments", file);
        }
      });
    }

    const response = await fetch(
      `${API_BASE}/api/appointments/updateCampaign`,
      {
        method: "PUT",
        headers: getFormDataHeaders(),
        body: formData,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Update campaign error:", error);
    throw error;
  }
}

/**
 * 9. Delete Campaign (DELETE)
 * DELETE /api/appointments/:campaignId
 */
export async function deleteCampaign(campaignId) {
  try {
    const response = await fetch(`${API_BASE}/api/appointments/${campaignId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Delete campaign error:", error);
    throw error;
  }
}
